import classNames from 'classnames';
import React, {
  forwardRef,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { asColor } from 'components/colorpicker/ColorPickerButton';
import { Hint, HintProps } from 'components/hint/Hint';
import { useDraggingState } from 'components/sortables/SortableContext';
import { ListColorSchemes } from 'helpers/colorSchemes';
import { xyCoord } from 'helpers/positions';
import { IconAnchors, IconSymbol, IconVariant } from './Icon.types';
import { getIconImage } from './iconImageCache';

import './Icon.scss';

export function getIconOffset(symbol: IconSymbol, size: number): xyCoord {
  const anchor = IconAnchors[symbol];
  if (anchor) {
    const viewBoxSize = anchor ? (anchor[2] ?? 24) : 24;
    const scale = size && viewBoxSize ? size / viewBoxSize : 1;
    const displacement = {
      x: anchor[0] * scale,
      y: anchor[1] * scale,
    };
    return displacement;
  }
  return { x: 0, y: 0 };
}

export function getIconSymbol(
  s: string | null | undefined,
  defaultValue: IconSymbol = IconSymbol.chat,
): IconSymbol {
  try {
    if (!s) return defaultValue;
    const k: keyof typeof IconSymbol = s as keyof typeof IconSymbol;
    return (
      IconSymbol[k] ?? IconSymbol[k.toLowerCase() as keyof typeof IconSymbol]
    );
  } catch (err) {
    return defaultValue;
  }
}

export function getSvgDataUrlFromSprite(
  iconId: IconSymbol,
  size: number = 32,
  color: string | null = null,
): string | null {
  // Find the sprite in the DOM
  const sprite = document.getElementById('icons-sprite');
  if (!sprite) return null;
  // Find the symbol
  const symbol = sprite.querySelector(`#${iconId}`);
  if (!symbol) return null;
  // Get symbol's inner markup
  let symbolMarkup = symbol.innerHTML;
  // Replace all 'currentColor' with the provided color
  if (color) {
    symbolMarkup = symbolMarkup.replace(/currentColor/gi, color);
  }
  // Get viewBox from symbol
  const viewBox = symbol.getAttribute('viewBox') || `0 0 ${size} ${size}`;
  // Compose SVG markup
  const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${size}" height="${size}">${symbolMarkup}</svg>`;
  // Get anchor point
  const anchor = getIconOffset(iconId, size);
  // Encode as data URL
  return `url('data:image/svg+xml;utf8,${encodeURIComponent(svgMarkup)}') ${anchor ? anchor.x : ''} ${anchor ? anchor.y : ''}, auto`;
}

export function setCursorToIcon(
  iconId: IconSymbol,
  size: number = 32,
  color: string | null = null,
) {
  const iconUrl = getSvgDataUrlFromSprite(iconId, size, color);
  if (iconUrl) {
    document.body.style.cursor = iconUrl;
  }
}

export function resetCursor() {
  document.body.style.cursor = 'auto';
}

type IconProps = {
  symbol: IconSymbol;
  variant?: IconVariant;
  hoverSymbol?: IconSymbol;
  hoverVariant?: IconVariant;
  color?: string;
  asImg?: boolean;
  className?: string;
  asTool?: boolean; // adds pd_tool class and enables pointer cursor (automatic when onClick is set)
  onClick?: MouseEventHandler;
  onDoubleClick?: MouseEventHandler;
  onMouseDown?: MouseEventHandler;
  onMouseOver?: MouseEventHandler;
  onMouseOut?: MouseEventHandler;
  disabled?: boolean;
  size?: number | 'max'; // size in pixels, sets width and height as element style
  style?: any; // element style, overrides width and height set through the size prop
  align?: 'top' | 'middle' | 'bottom'; // alignment within container, sets element style
  useAnchor?: boolean; // position absolute and use anchor point for positioning
  hintProps?: HintProps;
  blurBg?: boolean;
  inline?: boolean;
  zIndex?: number;
  jump?: boolean;
  debug?: boolean;
};

const Icon = forwardRef<HTMLDivElement | SVGSVGElement, IconProps>(
  function Icon(props, ref) {
    const {
      symbol,
      hoverSymbol,
      variant,
      hoverVariant,
      color,
      className,
      asTool,
      onClick,
      onDoubleClick,
      onMouseDown,
      onMouseOver,
      onMouseOut,
      disabled = false,
      size = 32,
      style,
      useAnchor = false,
      hintProps,
      inline,
      zIndex,
      jump,
      debug,
    } = props;
    // const triggerRef = useRef<HTMLDivElement | SVGSVGElement>(null);
    const [iconId, setIconId] = useState<string>('');
    const [iconClass, setIconClass] = useState<string | undefined>('');
    const [showJump, setShowJump] = useState(false);
    const { isPreview } = useDraggingState();
    const isTool = onClick ? true : !!asTool;
    const clr = color
      ? asColor(color, ListColorSchemes.canvas_btn).hex
      : undefined;
    const dataUrl = useMemo(
      () => (isPreview ? getIconImage(symbol, variant, clr) : null),
      [isPreview, symbol, variant, clr],
    );
    // const iconOffset = getIconOffset(symbol, size ?? 24);

    // Forward the ref to the triggerRef
    // useEffect(() => {
    //   if (!ref) return;
    //   if (typeof ref === 'function') {
    //     ref(triggerRef.current);
    //   } else {
    //     (
    //       ref as React.MutableRefObject<HTMLDivElement | SVGSVGElement | null>
    //     ).current = triggerRef.current;
    //   }
    // }, [triggerRef, ref]);

    useEffect(() => {
      if (symbol === IconSymbol._none) {
        setIconId('');
        setIconClass('icon_spacer');
        return;
      }
      setIconId(symbol);
      setIconClass(getClassNames(false));
    }, [symbol, variant]);

    // Manage jump animation timing: 2 runs (1.2s) + 15s wait
    useEffect(() => {
      if (!jump) {
        setShowJump(false);
        return;
      }
      // setShowJump(true);
      const timeout = setTimeout(() => {
        setShowJump(false);
        const interval = setInterval(() => {
          setShowJump(true);
          const innerTimeout = setTimeout(() => {
            setShowJump(false);
          }, 1200); // 2 runs × 0.6s per run
          return () => clearTimeout(innerTimeout);
        }, 6200); // 1.2s animation + 5s wait = 6.2s total
        return () => clearInterval(interval);
      }, 1200); // Initial animation: 2 runs × 0.6s
      return () => clearTimeout(timeout);
    }, [jump]);

    function getSymbol(hover: boolean) {
      return hover ? (hoverSymbol ?? symbol) : symbol;
    }
    function getVariant(hover: boolean) {
      return hover ? (hoverVariant ?? variant) : variant;
    }
    function getClassNames(hover: boolean) {
      const sym = getSymbol(hover);
      const vnt = getVariant(hover);
      return `icon_${sym}${vnt && vnt !== IconVariant.normal ? ' icon_var_' + vnt : ''}`;
    }

    const handleMouseOver = useCallback(
      (e: React.MouseEvent) => {
        if (!disabled && (hoverSymbol || hoverVariant)) {
          const sym = getSymbol(true);
          setIconId(sym);
          setIconClass(getClassNames(true));
        }
        onMouseOver && onMouseOver(e);
      },
      [onMouseOver, symbol, hoverSymbol, variant, hoverVariant],
    );

    const handleMouseOut = useCallback(
      (e: React.MouseEvent) => {
        if (!disabled && (hoverSymbol || hoverVariant)) {
          const sym = getSymbol(false);
          setIconId(sym);
          setIconClass(getClassNames(false));
        }
        onMouseOut && onMouseOut(e);
      },
      [onMouseOut, symbol, hoverSymbol, variant, hoverVariant],
    );

    return (
      <Hint placement="top" {...hintProps} adjusted={true}>
        <div
          className={classNames('pd_icon', className, iconClass, {
            pd_tool: isTool,
            inline: inline,
            icon_jump: showJump,
            anchored: useAnchor,
          })}
          onClick={!disabled ? onClick : undefined}
          onDoubleClick={!disabled ? onDoubleClick : undefined}
          style={{
            width: size === 'max' ? '100%' : size,
            height: size === 'max' ? '100%' : size,
            zIndex,
            color: clr,
            transition: 'none',
            // position: useAnchor ? 'absolute' : undefined,
            alignSelf: props.align
              ? props.align === 'top'
                ? 'flex-start'
                : props.align === 'bottom'
                  ? 'flex-end'
                  : 'center'
              : undefined,
            ...style,
          }}
        >
          {iconId ? (
            isPreview ? (
              <div
                className="pd_icon_bg"
                style={{
                  width: size === 'max' ? '100%' : size,
                  height: size === 'max' ? '100%' : size,
                  backgroundImage: `url(${dataUrl})`,
                }}
              />
            ) : (
              <svg
                ref={ref as React.RefObject<SVGSVGElement>}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
                style={{
                  width: size === 'max' ? '100%' : size,
                  height: size === 'max' ? '100%' : size,
                  verticalAlign: 'unset',
                  color: clr,
                }}
              >
                <use
                  xlinkHref={`#${iconId}`}
                  style={{ pointerEvents: 'none' }}
                />
              </svg>
            )
          ) : null}
        </div>
      </Hint>
    );
  },
);

export default React.memo(Icon);
